/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import * as yup from 'yup'
import { COMPLETE_STATUSES } from './EditFormConstants'

yup.setLocale({
  mixed: {
    required: 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
  },
  string: {
    max: 'FORM_VALIDATION_ERROR_MAX_LENGTH_IS_${max}'
  }
})

// Custom validation methods
declare module 'yup' {
  interface ArraySchema<T> {
    uniqueValues(otherField: string): ArraySchema<T>
  }
  interface NumberSchema {
    existsValue(...fields: string[]): NumberSchema
  }
}

yup.addMethod(yup.array, 'uniqueValues', function (this: any, otherField: string) {
  return this.test('uniqueValues', 'FORM_VALIDATION_ERROR_VALUES_MUST_BE_UNIQUE', function (this: any, value: any) {
    const { parent } = this
    const otherValue = parent[otherField]

    if (!value || !Array.isArray(value) || value.length === 0) {
      return true
    }

    if (!otherValue || !Array.isArray(otherValue) || otherValue.length === 0) {
      return true
    }

    // Check if any value exists in both arrays
    const intersection = value.filter((val: any) =>
      otherValue.some((otherVal: any) => {
        if (typeof val === 'object' && val !== null && 'value' in val) {
          return (
            typeof otherVal === 'object' && otherVal !== null && 'value' in otherVal && val.value === otherVal.value
          )
        }
        return val === otherVal
      })
    )

    return intersection.length === 0
  })
})

yup.addMethod(yup.number, 'existsValue', function (this: any, ...fields: string[]) {
  return this.test(
    'existsValue',
    'FORM_VALIDATION_ERROR_VALUE_MUST_EXIST_IN_SPECIFIED_FIELDS',
    function (this: any, value: any) {
      const { parent } = this

      if (!value) {
        return true
      }

      // Check if value exists in any of the specified fields
      for (const field of fields) {
        const fieldValue = parent[field]
        if (Array.isArray(fieldValue)) {
          const exists = fieldValue.some((item: any) => {
            if (typeof item === 'object' && item !== null && 'value' in item) {
              return item.value === value
            }
            return item === value
          })
          if (exists) {
            return true
          }
        }
      }

      return false
    }
  )
})

const ValidationSchema = () => {
  return yup.object().shape({
    // Basic fields
    name: yup
      .string()
      .trim()
      .max(255)
      .required()
      .default(() => ''),

    description: yup
      .string()
      .max(255)
      .nullable()
      .default(() => ''),

    // Custom effective state label
    _customEffectiveStateLabelType: yup
      .string()
      .max(255)
      .nullable()
      .default(() => COMPLETE_STATUSES.EFFECTIVE),

    _customEffectiveStateLabelText: yup
      .string()
      .max(255)
      .nullable()
      .default(() => ''),

    customEffectiveStateLabel: yup
      .string()
      .max(255)
      .nullable()
      .default(() => ''),

    // Auto-naming fields
    autonamingEnabled: yup
      .boolean()
      .nullable()
      .default(() => false),

    autonamingTemplate: yup
      .string()
      .max(255)
      .nullable()
      .when('autonamingEnabled', {
        is: true,
        then: (schema: any) => schema.required()
      })
      .default(() => ''),

    autonamingSeqNo: yup
      .number()
      .min(0)
      .nullable()
      .transform((value: any) => (isNaN(value) ? null : value))
      .when('autonamingEnabled', {
        is: true,
        then: (schema: any) => schema.required()
      }),
    // Effective time
    effectiveTimeDaysEnabled: yup
      .boolean()
      .nullable()
      .default(() => false),

    effectiveTimeDays: yup
      .number()
      // 1. Always transform empty strings to undefined first
      .transform((value: any, originalValue: any) => (originalValue === '' ? undefined : value))
      .when('effectiveTimeDaysEnabled', {
        is: true,
        // 2. Apply strict validation only when enabled
        then: (schema: any) =>
          schema
            .typeError('Must be a number')
            .min(0, 'Value must be at least 0')
            .max(255, 'FORM_VALIDATION_ERROR_MAX_LENGTH_IS_255')
            .required('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'),
        // 3. When disabled, allow it to be null/empty
        otherwise: (schema: any) => schema.nullable().notRequired()
      }),

    // Periodic review / Expiration
    expirationTimeMonthsEnabled: yup
      .boolean()
      .nullable()
      .default(() => false),

    expirationTimeMonths: yup
      .number()
      // 1. Always transform empty strings to undefined first
      .transform((value: any, originalValue: any) => (originalValue === '' ? undefined : value))
      .when('expirationTimeMonthsEnabled', {
        is: true,
        // 2. Apply strict validation only when enabled
        then: (schema: any) =>
          schema
            .typeError('Must be a number')
            .min(0, 'Value must be at least 0')
            .max(255, 'FORM_VALIDATION_ERROR_MAX_LENGTH_IS_255')
            .required('GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'),
        // 3. When disabled, allow it to be null/empty
        otherwise: (schema: any) => schema.nullable().notRequired()
      }),

    isPeriodicReviewCustomDateEnabled: yup
      .boolean()
      .nullable()
      .default(() => false),

    customLastDateMetadataTemplate: yup
      .number()
      .nullable()
      .when(['isPeriodicReviewCustomDateEnabled', 'expirationTimeMonthsEnabled'], {
        is: (custom: boolean, enabled: boolean) => custom && enabled,
        then: (schema: any) => schema.existsValue('optionalMetadataTemplates', 'requiredMetadataTemplates').required()
      })
      .default(() => null),

    customNextDateMetadataTemplate: yup
      .number()
      .nullable()
      .when(['isPeriodicReviewCustomDateEnabled', 'expirationTimeMonthsEnabled'], {
        is: (custom: boolean, enabled: boolean) => custom && enabled,
        then: (schema: any) => schema.existsValue('optionalMetadataTemplates', 'requiredMetadataTemplates').required()
      })
      .default(() => null),

    // Boolean flags
    addSignaturePage: yup
      .boolean()
      .nullable()
      .default(() => false),

    addSignaturePageToFront: yup
      .boolean()
      .nullable()
      .default(() => false),

    allowOverridesDuringCR: yup
      .boolean()
      .nullable()
      .default(() => false),

    requireJustification: yup
      .boolean()
      .nullable()
      .default(() => false),

    requireRejectReason: yup
      .boolean()
      .nullable()
      .default(() => false),

    allowPropertiesChangeDuringEdit: yup
      .boolean()
      .nullable()
      .default(() => true),

    allowPropertiesChangeDuringReview: yup
      .boolean()
      .nullable()
      .default(() => false),

    amendmentsEnabled: yup
      .boolean()
      .nullable()
      .default(() => false),

    allowBlindingStatusChange: yup
      .boolean()
      .nullable()
      .default(() => false),

    // Due date configuration (MVP - full parity)
    dueDateEnabled: yup
      .boolean()
      .nullable()
      .default(() => false),

    dueDateOffset: yup
      .number()
      .nullable()
      .transform((value: any) => (isNaN(value) ? null : value))
      .when('dueDateEnabled', {
        is: true,
        then: (schema: any) => schema.min(0).max(255, 'Must be less than or equal to 255').required(),
        otherwise: (schema: any) => schema.nullable()
      }),

    // Metadata templates (arrays of select options)
    optionalMetadataTemplates: yup
      .array()
      .uniqueValues('requiredMetadataTemplates')
      .of(
        yup.object().shape({
          value: yup.number().required(),
          label: yup.string().required()
        })
      )
      .nullable()
      .default(() => []),

    requiredMetadataTemplates: yup
      .array()
      .uniqueValues('optionalMetadataTemplates')
      .of(
        yup.object().shape({
          value: yup.number().required(),
          label: yup.string().required()
        })
      )
      .nullable()
      .default(() => []),

    // Watermarks
    watermarkIds: yup
      .array()
      .of(
        yup.object().shape({
          value: yup.number().required(),
          label: yup.string().required()
        })
      )
      .nullable()
      .default(() => []),

    // Template files - accept either number or object with value/label, transform to number
    signatureTemplateFileId: yup
      .mixed()
      .nullable()
      .transform((value: any) => {
        if (!value) return null
        if (typeof value === 'object' && 'value' in value) {
          return Number(value.value)
        }
        return Number(value)
      })
      .test('is-number', 'Must be a valid number', (value: any) => {
        return value === null || !isNaN(value)
      }),

    templateFileId: yup
      .mixed()
      .nullable()
      .transform((value: any) => {
        if (!value) return null
        if (typeof value === 'object' && 'value' in value) {
          return Number(value.value)
        }
        return Number(value)
      })
      .test('is-number', 'Must be a valid number', (value: any) => {
        return value === null || !isNaN(value)
      }),

    // Workflow template (select option with value/label)
    workflowTemplate: yup
      .object()
      .shape({
        value: yup.number().required(),
        label: yup.string().required()
      })
      .nullable()
      .default(() => null),

    // ID (for edit mode)
    id: yup.number().nullable()
  })
}

export default ValidationSchema
